'use client';

import { useEffect, useState } from 'react';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { Sparkles, ThumbsDown, ThumbsUp, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { aiSearchKey, type AiNote } from '@/lib/ai-search';
import { parseBound } from '@/lib/search-filters';
import { aiSearchEntrySchema, useAiSearchFeedback } from '@/hooks/use-ai-search';
import { Button } from '@/components/ui/button';

interface Entry {
  url: string; query: string; notes: AiNote[]; unmapped: string[]; id: number | null; charged: number;
}

const DISPLAYED_BACKEND_NOTES = new Set(['revenue_sparse', 'nace_refined', 'rejected']);

function formatBound(value: number | null | undefined, locale: string, million: string) {
  if (value == null) return '∞';
  const scaled = Math.abs(value) >= 1_000_000 ? value / 1_000_000 : value;
  return `${new Intl.NumberFormat(locale, { maximumFractionDigits: 1 }).format(scaled)}${Math.abs(value) >= 1_000_000 ? ` ${million}` : ''}`;
}

function formatRange(range: { min?: number | null; max?: number | null }, locale: string, million: string) {
  if (range.min != null && range.max != null &&
    Math.abs(range.min) >= 1_000_000 && Math.abs(range.max) >= 1_000_000) {
    const number = new Intl.NumberFormat(locale, { maximumFractionDigits: 1 });
    return `${number.format(range.min / 1_000_000)}–${number.format(range.max / 1_000_000)} ${million}`;
  }
  return `${formatBound(range.min, locale, million)}–${formatBound(range.max, locale, million)}`;
}

export function AiSearchBanner() {
  const { t, i18n } = useTranslation();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const router = useRouter();
  const feedback = useAiSearchFeedback();
  const key = aiSearchKey(new URLSearchParams(searchParams.toString()));
  const [entry, setEntry] = useState<Entry | null>(null);
  const [loadedKey, setLoadedKey] = useState<string | null>(null);
  const [voted, setVoted] = useState(false);
  const [selectedVote, setSelectedVote] = useState<-1 | 1 | null>(null);

  useEffect(() => {
    const readEntry = () => {
      try {
        const raw = sessionStorage.getItem(`ai-search:${key}`);
        const parsed = aiSearchEntrySchema.safeParse(raw ? JSON.parse(raw) : null);
        if (parsed.success) {
          setEntry(parsed.data);
          setLoadedKey(key);
          const savedVote = parsed.data.id === null ? null : sessionStorage.getItem(`ai-feedback:${parsed.data.id}`);
          setVoted(savedVote !== null);
          setSelectedVote(savedVote === '1' ? 1 : savedVote === '-1' ? -1 : null);
        } else { setEntry(null); setLoadedKey(null); }
      } catch { setEntry(null); setLoadedKey(null); }
    };
    const timer = window.setTimeout(readEntry, 0);
    window.addEventListener('firmendata:ai-search-banner', readEntry);
    return () => {
      window.clearTimeout(timer);
      window.removeEventListener('firmendata:ai-search-banner', readEntry);
    };
  }, [key]);

  if (!entry || loadedKey !== key) return null;
  const requestedMax = parseBound(searchParams.get('revenueMax'));
  const canUseSize = requestedMax !== undefined && requestedMax <= 50_000_000;
  const rewrite = (mode: 'assets' | 'size') => {
    const params = new URLSearchParams(searchParams.toString());
    const min = params.get('revenueMin');
    const max = params.get('revenueMax');
    params.delete('revenueMin');
    params.delete('revenueMax');
    params.delete('revenueIncludeNull');
    params.delete('offset');
    if (mode === 'assets') {
      params.delete('aktivaMin'); params.delete('aktivaMax');
      if (min !== null) params.set('aktivaMin', min);
      if (max !== null) params.set('aktivaMax', max);
    } else {
      params.delete('companySize'); params.append('companySize', 'kleinst'); params.append('companySize', 'klein'); params.append('companySize', 'mittelgross');
    }
    router.push(`${pathname}?${params}`);
  };
  const vote = (value: -1 | 1) => {
    if (voted || entry.id === null) return;
    feedback.mutate({ id: entry.id, value }, { onSuccess: () => {
      setVoted(true);
      setSelectedVote(value);
      try { sessionStorage.setItem(`ai-feedback:${entry.id}`, String(value)); } catch { /* unavailable storage */ }
    }, onError: () => toast.error(t('search.ai.feedbackFailed')) });
  };
  return (
    <div role="status" className="m-3 rounded-lg border border-primary/30 bg-primary/5 p-3 text-sm">
      <div className="flex items-start gap-2">
        <Sparkles className="mt-0.5 size-4 shrink-0" aria-hidden />
        <div className="min-w-0 flex-1">
          <strong>{t('search.ai.interpreted', { query: entry.query })}</strong>
          {entry.notes.map((note, index) => {
            if (note.kind === 'rounded') return <p key={index}>{t('search.ai.rounded', {
              field: t(`search.ai.fields.${note.field}`),
              requested: formatRange(note.requested, i18n.language, t('search.ai.million')),
              applied: formatRange(note.applied, i18n.language, t('search.ai.million')),
            })}</p>;
            if (note.kind === 'dropped') return <p key={index}>{t('search.ai.dropped', { field: t(`search.ai.fields.${note.field}`) })}</p>;
            if (note.kind === 'sort_unsupported') return <p key={index}>{t('search.ai.sortUnsupported')}</p>;
            if (note.kind === 'unmapped') return <p key={index}>{t('search.ai.unmapped', { items: note.text })}</p>;
            if (note.code === 'city_unresolved') return null;
            return <p key={index}>{t(DISPLAYED_BACKEND_NOTES.has(note.code)
              ? `search.ai.notes.${note.code}` : 'search.ai.notes.other')}</p>;
          })}
          {entry.notes.some((note) => note.kind === 'backend' && note.code === 'revenue_sparse') && (searchParams.has('revenueMin') || searchParams.has('revenueMax')) && (
            <div className="mt-2 flex flex-wrap gap-2">
              <Button type="button" variant="outline" size="sm" className="min-h-10 cursor-pointer lg:min-h-7" onClick={() => rewrite('assets')}>{t('search.ai.useAssets')}</Button>
              {canUseSize && <Button type="button" variant="outline" size="sm" className="min-h-10 cursor-pointer lg:min-h-7" onClick={() => rewrite('size')}>{t('search.ai.useSize')}</Button>}
            </div>
          )}
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <Button type="button" variant="outline" size="sm" className="min-h-10 cursor-pointer lg:min-h-7" onClick={() => window.dispatchEvent(new CustomEvent('firmendata:edit-ai-search', { detail: entry.query }))}>{t('search.ai.edit')}</Button>
            {entry.id !== null && <>
              <Button type="button" variant="ghost" size="icon" className={`size-10 cursor-pointer lg:size-8.5 ${selectedVote === 1 ? 'text-primary disabled:opacity-100' : ''}`} aria-label={t('search.ai.helpful')} aria-pressed={selectedVote === 1} disabled={voted || feedback.isPending} onClick={() => vote(1)}><ThumbsUp className={`size-4 ${selectedVote === 1 ? 'fill-current' : ''}`} /></Button>
              <Button type="button" variant="ghost" size="icon" className={`size-10 cursor-pointer lg:size-8.5 ${selectedVote === -1 ? 'text-primary disabled:opacity-100' : ''}`} aria-label={t('search.ai.unhelpful')} aria-pressed={selectedVote === -1} disabled={voted || feedback.isPending} onClick={() => vote(-1)}><ThumbsDown className={`size-4 ${selectedVote === -1 ? 'fill-current' : ''}`} /></Button>
            </>}
            {voted && <span className="text-xs text-muted-foreground">{t('search.ai.feedbackThanks')}</span>}
          </div>
        </div>
        <Button type="button" variant="ghost" size="icon" className="size-10 shrink-0 cursor-pointer lg:size-8.5" aria-label={t('common.buttons.close')} onClick={() => { try { sessionStorage.removeItem(`ai-search:${key}`); } catch { /* unavailable storage */ } setEntry(null); setLoadedKey(null); }}><X className="size-4" /></Button>
      </div>
    </div>
  );
}
